import { useGT } from 'gt-react';
import type { ForecastFactorRow } from 'src/api/rpc/hooks/procurement/useForecastFactors';
import {
  OverrideHistoryList,
  OverrideHistoryArchive,
  OverrideHistoryRow,
  OverrideRowActions,
  OverrideStatusBadges,
} from '../components/OverrideHistory';
import {
  overrideDeactivationLine,
  overrideMetaLine,
  replacedOverrideIds,
} from '../components/overrideRows';
import { formatFactorPercent } from '../components/forecastFactorFormat';
import {
  factorTextClasses,
  isApplying,
  statusBadgeClasses,
  statusLabel,
  statusOf,
  type FactorStatus,
} from './forecastFactorRows';
import {
  factorUsage,
  type FactorPeriod,
  type FactorUsage,
} from './forecastFactorSchedule';
import { factorPeriodLabel } from './ForecastFactorTimeline';

// Every growth/shrinkage factor ever set for one series, newest first, with
// its status: the row chain IS the history, so an edited row shows as
// "replaced" rather than changing in place. The row layout is shared with the
// other override lists; what belongs here is the percent as the headline and
// the factor status vocabulary. See docs/procurement/growth-shrinkage-factors.md.

function ForecastFactorHistoryRow({
  override: o,
  status,
  usage,
  onEdit,
  onRequestRevoke,
}: {
  override: ForecastFactorRow;
  status: FactorStatus;
  usage: FactorUsage;
  onEdit: (o: ForecastFactorRow) => void;
  onRequestRevoke: (o: ForecastFactorRow) => void;
}) {
  const gt = useGT();
  return (
    <OverrideHistoryRow
      headline={
        <span className={factorTextClasses(status)}>
          {gt('{pct} (x{factor})', {
            context: 'growth factor row headline, e.g. "+15% (x1.15)"',
            pct: formatFactorPercent(o.factor),
            factor: String(o.factor),
          })}
        </span>
      }
      status={
        <OverrideStatusBadges
          label={statusLabel(status, gt)}
          className={statusBadgeClasses(status)}
          filled={isApplying(status)}
          needsReview={o.needsReview}
        />
      }
      meta={gt('Configured: {window}', {
        context: 'growth factor configured window and author',
        window: overrideMetaLine(o, gt, 'month'),
      })}
      usage={
        o.isActive ? (
          <>
            {usage.periods.length > 0
              ? gt('Applies to demand: {months}', {
                  context:
                    'growth factor winning months from the current month onward',
                  months: usage.periods
                    .map((p) => factorPeriodLabel(p, gt))
                    .join('; '),
                })
              : gt(
                  'Newer overrides cover the entire remaining window. Kept as a fallback if they are revoked.',
                  {
                    context: 'growth factor fully overridden explanation',
                  },
                )}
            {status === 'partial' ? (
              <p>
                {gt(
                  'Newer overrides apply in the other months of the remaining window.',
                  { context: 'growth factor partially overridden explanation' },
                )}
              </p>
            ) : null}
          </>
        ) : null
      }
      deactivation={overrideDeactivationLine(o, status === 'replaced', gt)}
      actions={
        o.isActive ? (
          <OverrideRowActions
            override={o}
            onEdit={onEdit}
            onRequestRevoke={onRequestRevoke}
          />
        ) : null
      }
    />
  );
}

export function ForecastFactorHistory({
  overrides,
  schedule,
  onEdit,
  onRequestRevoke,
}: {
  overrides: readonly ForecastFactorRow[];
  schedule: readonly FactorPeriod[];
  onEdit: (o: ForecastFactorRow) => void;
  onRequestRevoke: (o: ForecastFactorRow) => void;
}) {
  if (overrides.length === 0) return null;
  const replacedIds = replacedOverrideIds(overrides);
  const live = overrides.filter((o) => o.isActive);
  const history = overrides.filter((o) => !o.isActive);
  const renderRow = (o: ForecastFactorRow) => {
    const usage = factorUsage(o, schedule);
    return (
      <ForecastFactorHistoryRow
        key={o.id}
        override={o}
        status={statusOf(o, usage.status, replacedIds)}
        usage={usage}
        onEdit={onEdit}
        onRequestRevoke={onRequestRevoke}
      />
    );
  };
  return (
    <>
      {live.length > 0 ? (
        <OverrideHistoryList>{live.map(renderRow)}</OverrideHistoryList>
      ) : null}
      <OverrideHistoryArchive count={history.length}>
        {history.map(renderRow)}
      </OverrideHistoryArchive>
    </>
  );
}
